import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Mail, Lock, Key, Apple, CheckCircle2, Circle, Clock, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';

const ActivityFeed = ({ updates }) => {
    if (!updates || updates.length === 0) return (
        <div className="p-6 bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl">
            <h3 className="font-bold text-[#F0EFF4] mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#7B61FF]" />
                Build Log
            </h3>
            <p className="text-sm text-[#F0EFF4]/40 italic">Waiting for build initialization...</p>
        </div>
    );

    return (
        <div className="p-6 bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl flex flex-col h-full max-h-[800px]">
            <h3 className="font-bold text-[#F0EFF4] mb-6 flex items-center gap-2 shrink-0">
                <MessageSquare className="w-4 h-4 text-[#7B61FF]" />
                Activity Feed
            </h3>

            <div className="space-y-6 overflow-y-auto pr-2 custom-scrollbar">
                {updates.map((update, i) => {
                    const isVito = update.source === 'vito_agent';
                    const icon = isVito ? '🚬' : (update.type === 'milestone' ? '🏆' : update.type === 'link' ? '👀' : update.type === 'shipped' ? '📦' : 'ℹ️');

                    return (
                        <div key={i} className="relative pl-6 before:absolute before:left-2 before:top-6 before:bottom-[-24px] before:w-px before:bg-[#F0EFF4]/10 last:before:hidden">
                            <div className="absolute left-0 top-1 w-4 h-4 flex items-center justify-center bg-[#18181B] z-10 text-sm">
                                {icon}
                            </div>
                            <div className={`p-4 rounded-xl border ${isVito ? 'border-[#7B61FF]/30 bg-[#7B61FF]/5' : 'border-[#F0EFF4]/5 bg-[#0A0A14]'}`}>
                                <div className="text-xs text-[#F0EFF4]/40 mb-1 flex justify-between">
                                    <span className="font-bold uppercase tracking-wider">{isVito ? 'Vito' : 'System'}</span>
                                    <span>{new Date(update.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                                </div>
                                <p className="text-[#F0EFF4]/90 text-sm">{update.message}</p>
                                {update.link_url && (
                                    <a href={update.link_url} target="_blank" rel="noreferrer" className="inline-block mt-3 text-xs font-bold text-[#7B61FF] hover:translate-y-[-1px] transition-transform">
                                        {update.link_label || 'View Link'} &rarr;
                                    </a>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {/* Custom scrollbar styles would be in index.css */}
        </div>
    );
};

const StepCard = ({ icon: Icon, title, isCompleted, isHandled, isActive, isLocked, children, completionMessage }) => {
    const [expanded, setExpanded] = useState(isActive);

    // Auto-expand if active, otherwise user can toggle if completed. Locked is always collapsed.
    const toggle = () => {
        if (!isLocked && !isHandled) {
            setExpanded(!expanded);
        }
    };

    return (
        <div className={`border rounded-2xl overflow-hidden transition-all duration-300 ${isActive ? 'border-[#7B61FF]/50 bg-[#18181B] shadow-[0_4px_24px_-8px_rgba(123,97,255,0.2)]' :
                isCompleted ? 'border-[#F0EFF4]/10 bg-[#0A0A14]/50' :
                    isHandled ? 'border-[#F0EFF4]/5 bg-[#0A0A14]/50 opacity-70' :
                        'border-[#F0EFF4]/5 bg-[#0A0A14]/20 opacity-40'
            }`}>
            <div
                className={`p-5 flex items-center justify-between ${(!isLocked && !isHandled) ? 'cursor-pointer hover:bg-[#F0EFF4]/[0.02]' : ''} transition-colors`}
                onClick={toggle}
            >
                <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isActive ? 'bg-[#7B61FF]/20 text-[#7B61FF]' :
                            isCompleted ? 'bg-emerald-500/20 text-emerald-400' :
                                'bg-[#F0EFF4]/5 text-[#F0EFF4]/40'
                        }`}>
                        {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <div>
                        <h4 className={`font-bold text-lg ${isActive ? 'text-[#F0EFF4]' : isCompleted ? 'text-[#F0EFF4]/90' : 'text-[#F0EFF4]/60'}`}>
                            {title}
                        </h4>
                        <div className="text-xs font-data uppercase tracking-wider mt-1">
                            {isActive && <span className="text-[#7B61FF]">Active</span>}
                            {isCompleted && <span className="text-emerald-400">Completed</span>}
                            {isHandled && <span className="text-[#F0EFF4]/40">We handle this</span>}
                            {isLocked && <span className="text-[#F0EFF4]/40 flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>}
                        </div>
                    </div>
                </div>
                {(!isLocked && !isHandled) && (
                    <div className="text-[#F0EFF4]/30">
                        {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                )}
            </div>

            <div className={`grid transition-all duration-300 ease-in-out ${expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                <div className="overflow-hidden">
                    <div className="p-6 pt-0 border-t border-[#F0EFF4]/5 mt-2">
                        {isCompleted && completionMessage ? (
                            <div className="px-4 py-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl text-sm font-medium">
                                {completionMessage}
                            </div>
                        ) : null}

                        <div className={`space-y-4 ${isCompleted ? 'mt-6 opacity-60' : 'mt-4'}`}>
                            {children}
                        </div>
                    </div>
                </div>
            </div>

            {/* If it's handled and not expanded, we still render a quick snippet if provided? No, the spec says "greyed out with 'Our team is on it' label" */}
            {isHandled && (
                <div className="px-5 pb-5 pl-[72px] text-sm text-[#F0EFF4]/40">
                    {children}
                </div>
            )}
        </div>
    );
};

const BuildStatus = () => {
    const { clientData } = useOutletContext();
    const completed = clientData?.completed_steps || [];
    const updates = clientData?.client_updates || [];
    const clientFirstName = (clientData?.name || '').split(' ')[0];
    const clientCompany = clientData?.company || '';

    // Check states
    const emailDone = completed.includes('agent_email');
    const tailscaleDone = completed.includes('tailscale');
    const credsDone = completed.includes('credentials');
    const appleIdDone = completed.includes('apple_id');

    const allPreReqsDone = emailDone && tailscaleDone && credsDone && appleIdDone;

    // Determine active phase (1: Pre-reqs, 2: Pre-Consult, 3: Build, 4: Live)
    let activePhase = 1;
    if (clientData?.stage === 'Delivered') activePhase = 4;
    else if (clientData?.stage === 'In Build') activePhase = 3;
    else if (clientData?.stage === 'Pre-consult Scheduled') activePhase = 2;
    else if (allPreReqsDone) activePhase = 2; // Pre-reqs done, waiting to schedule

    const phases = [
        { num: 1, title: 'Pre-Reqs' },
        { num: 2, title: 'Pre-Consult' },
        { num: 3, title: 'Build' },
        { num: 4, title: 'Live' }
    ];

    return (
        <div className="animate-fade-in max-w-6xl mx-auto pb-24">
            {/* Header Area */}
            <header className="mb-12">
                <h1 className="text-3xl font-bold mb-3 tracking-tight">Onboarding Portal</h1>
                <p className="text-[#F0EFF4]/60 text-lg max-w-2xl">
                    Every step here brings your agent closer to life. Complete your pre-requisites to begin the build process.
                </p>
            </header>

            <div className="flex flex-col lg:flex-row gap-10">
                {/* Left Nav Indicator (Desktop) */}
                <div className="hidden lg:flex flex-col gap-6 w-48 shrink-0 sticky top-24 h-max">
                    <div className="text-xs font-data font-bold tracking-wider text-[#F0EFF4]/40 uppercase mb-2">Phase Progress</div>
                    {phases.map((p) => {
                        const isPast = p.num < activePhase;
                        const isCurrent = p.num === activePhase;

                        return (
                            <div key={p.num} className="flex items-center gap-4">
                                <div className={`w-3 h-3 rounded-full flex-shrink-0 transition-colors ${isCurrent ? 'bg-[#7B61FF] shadow-[0_0_12px_rgba(123,97,255,0.6)]' :
                                        isPast ? 'bg-emerald-400' : 'bg-[#18181B] border border-[#F0EFF4]/20'
                                    }`} />
                                <span className={`font-semibold text-sm transition-colors ${isCurrent ? 'text-[#F0EFF4]' :
                                        isPast ? 'text-[#F0EFF4]/70' : 'text-[#F0EFF4]/30'
                                    }`}>
                                    Phase {p.num}: {p.title}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Main Content */}
                <div className="flex-1 space-y-12">
                    {/* Phase 1: Pre-Reqs */}
                    <div className={activePhase > 1 ? 'opacity-60' : ''}>
                        <div className="mb-6 lg:hidden">
                            <span className="text-[#7B61FF] font-data font-bold tracking-wider uppercase text-xs">Phase 1</span>
                            <h2 className="text-2xl font-bold mt-1">Pre-Requisites</h2>
                        </div>
                        <div className="hidden lg:block mb-6">
                            <h2 className="text-2xl font-bold">Phase 1: Pre-Requisites</h2>
                        </div>

                        <div className="space-y-4">
                            <StepCard
                                icon={Mail}
                                title="Create Your Agent Email"
                                isCompleted={emailDone}
                                isActive={!emailDone}
                                completionMessage={`Perfect — your agent's email is locked in. It has a home base.`}
                            >
                                <p className="text-sm text-[#F0EFF4]/70 leading-relaxed">
                                    <strong className="text-[#F0EFF4]">Why this matters:</strong> Your AI agent needs its own professional email address — separate from yours. This is the address it'll use to send estimates, follow-ups, and client communications on your behalf. We strongly recommend creating one at your own domain (like <code className="bg-[#18181B] px-1.5 py-0.5 rounded text-[#7B61FF]">agent@yourcompany.com</code>) — it looks professional and keeps communication branded.
                                </p>
                                <div className="p-4 bg-[#7B61FF]/10 border border-[#7B61FF]/20 rounded-xl mt-4">
                                    <strong className="block text-[#7B61FF] text-sm mb-2">What to do:</strong>
                                    <p className="text-sm text-[#F0EFF4]/80">
                                        Create a new email address via your email provider (Google Workspace, Outlook, etc). You'll provide these details during your Pre-Consult Call. If you need help, reply to any email from Vito and we'll walk you through it in 10 minutes.
                                    </p>
                                    {!emailDone && (
                                        <div className="mt-4 pt-4 border-t border-[#7B61FF]/20">
                                            <p className="text-xs text-[#F0EFF4]/60 italic">We will mark this complete on our end once confirmed.</p>
                                        </div>
                                    )}
                                </div>
                            </StepCard>

                            <StepCard
                                icon={Lock}
                                title="Set Up Tailscale"
                                isHandled={true}
                                isCompleted={tailscaleDone}
                            >
                                <p className="leading-relaxed">
                                    Tailscale is the invisible security layer that makes RoofClaw possible. We handle adding your agent as a user during your build session. You don't need to do anything here.
                                    {tailscaleDone && <span className="block mt-2 text-emerald-400 font-medium text-xs">Tailscale is ready. Your private network is standing by. 🔐</span>}
                                </p>
                            </StepCard>

                            <StepCard
                                icon={Key}
                                title="Share Your API Credentials"
                                isCompleted={credsDone}
                                isActive={emailDone && !credsDone} // Active if previous is done? Or all active at once? Pre-reqs can be done in any order, so active if not done.
                                completionMessage="Credentials received and locked. Adam will have everything he needs before your call."
                            >
                                <p className="text-sm text-[#F0EFF4]/70 leading-relaxed">
                                    <strong className="text-[#F0EFF4]">Why this matters:</strong> Your agent's power comes from what it's connected to. Your CRM. Your job management software. Your calendar. The more you plug in, the more it can do without you having to touch it. This step is about getting those credentials ready before your call, so your build session is spent building — not hunting for passwords.
                                </p>
                                <div className="p-4 bg-[#18181B] border border-[#F0EFF4]/10 rounded-xl mt-4 space-y-3">
                                    <strong className="block text-[#F0EFF4] text-sm">What to do:</strong>
                                    <ol className="list-decimal list-inside text-sm text-[#F0EFF4]/80 space-y-2">
                                        <li>Identify the 3 tools you want your agent connected to first.</li>
                                        <li>Gather the API keys or login credentials for each.</li>
                                        <li>Submit them via Password Pusher securely.</li>
                                    </ol>
                                    {!credsDone && (
                                        <a href="https://pwpush.com" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-[#F0EFF4] hover:bg-[#7B61FF] text-[#0A0A14] hover:text-[#F0EFF4] text-sm font-bold rounded-xl transition-all hover:-translate-y-0.5 shadow-lg">
                                            Open Password Pusher &rarr;
                                        </a>
                                    )}
                                </div>
                            </StepCard>

                            <StepCard
                                icon={Apple}
                                title="Apple ID"
                                isHandled={true}
                                isCompleted={appleIdDone}
                            >
                                <p className="leading-relaxed">
                                    We'll create a dedicated Apple ID for your agent during your build session. You don't need to do anything here — we set it up on the machine, configure two-factor authentication, and hand it over to you after delivery.
                                </p>
                            </StepCard>
                        </div>
                    </div>

                    {/* Phase 2: Pre-Consult Call */}
                    <div className={activePhase > 2 ? 'opacity-60' : activePhase < 2 ? 'opacity-30' : ''}>
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold">Phase 2: Pre-Consult Call</h2>
                        </div>
                        <StepCard
                            icon={CheckCircle2}
                            title="Pre-Consult Call with Adam"
                            isLocked={activePhase < 2}
                            isActive={activePhase === 2}
                            isCompleted={activePhase > 2}
                            completionMessage="Pre-consult complete. Your agent is now being designed. The build begins. 🔥"
                        >
                            <p className="text-sm text-[#F0EFF4]/70 leading-relaxed mb-4">
                                <strong className="text-[#F0EFF4]">Why this matters:</strong> This is the session where your agent stops being a product and starts becoming a person. Adam will ask about your business, your frustrations, your goals, and your workflow — and from that, he'll design an agent identity that actually fits.
                            </p>
                            <div className="p-4 border border-[#F0EFF4]/10 rounded-xl">
                                <strong className="block text-sm text-[#F0EFF4] mb-2">What to do:</strong>
                                <ul className="list-disc list-inside text-sm text-[#F0EFF4]/80 space-y-1">
                                    <li>Watch your email for a scheduling link from Adam.</li>
                                    <li>Come prepared with the 3 tools you want connected.</li>
                                    <li>Bring ideas for your agent's name.</li>
                                </ul>
                            </div>
                        </StepCard>
                    </div>

                    {/* Phase 3: Build */}
                    <div className={activePhase > 3 ? 'opacity-60' : activePhase < 3 ? 'opacity-30' : ''}>
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold">Phase 3: The Build</h2>
                        </div>
                        <div className="p-8 border border-[#F0EFF4]/10 rounded-2xl bg-[#0A0A14] shadow-inner text-center">
                            {activePhase < 3 ? (
                                <p className="text-[#F0EFF4]/40 font-medium"><Lock className="w-4 h-4 mx-auto mb-2" /> Unlocks after Pre-Consult Call</p>
                            ) : activePhase === 3 ? (
                                <div>
                                    <div className="w-16 h-16 border-t-2 border-[#7B61FF] border-r-2 border-transparent rounded-full animate-spin mx-auto mb-6"></div>
                                    <h3 className="text-xl font-bold mb-2">Your Agent is Being Built</h3>
                                    <p className="text-[#F0EFF4]/60 max-w-md mx-auto text-sm leading-relaxed">
                                        This phase is passive for you. While you wait, we're writing your agent's identity files, connecting your integrations, and hardening the security. Watch the Activity Feed on the right for live updates from the build floor.
                                    </p>
                                </div>
                            ) : (
                                <div className="text-emerald-400">
                                    <CheckCircle2 className="w-10 h-10 mx-auto mb-3" />
                                    <h3 className="text-xl font-bold">Build Complete</h3>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Phase 4: Delivery */}
                    <div className={activePhase < 4 ? 'opacity-30' : ''}>
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold">Phase 4: Delivery & Training</h2>
                        </div>
                        <div className="p-8 border border-[#F0EFF4]/10 rounded-2xl bg-[#18181B]">
                            {activePhase < 4 ? (
                                <p className="text-[#F0EFF4]/40 font-medium text-center"><Lock className="w-4 h-4 mx-auto mb-2" /> Unlocks after Build Complete</p>
                            ) : (
                                <div>
                                    <h3 className="text-xl font-bold mb-3 text-[#7B61FF]">Your Machine Has Arrived.</h3>
                                    <p className="text-[#F0EFF4]/70 text-sm leading-relaxed mb-6">
                                        Now comes the fun part. Adam or Claudio will join you for a 3-hour live session where your agent comes to life in your hands. By the end of this call, your agent will be responding to messages, running connected tasks, and ready for day one.
                                    </p>
                                    <div className="bg-[#0A0A14] p-4 rounded-xl border border-[#F0EFF4]/5">
                                        <p className="text-sm font-semibold mb-2">Ongoing Support</p>
                                        <p className="text-sm text-[#F0EFF4]/60">You have 24/7 access to your agent via your VIP channel, plus a seat at <strong className="text-[#F0EFF4]">Winning Wednesday</strong> — our weekly live Q&A.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Rail: Activity Feed */}
                <div className="w-full lg:w-80 shrink-0">
                    <div className="sticky top-24">
                        <ActivityFeed updates={updates} />

                        <div className="mt-6 p-5 bg-[#18181B] border border-[#F0EFF4]/5 rounded-2xl">
                            <h4 className="font-bold text-[#F0EFF4] text-sm mb-1">Questions?</h4>
                            <p className="text-xs text-[#F0EFF4]/50 mb-4">Email your concierge — we typically reply within 15 mins.</p>
                            <a href={`mailto:vitoclawleone@roofingbusinesspartner.com?subject=${encodeURIComponent(`Portal Question — ${[clientFirstName, clientCompany].filter(Boolean).join(' / ') || 'Client'}`)}`} target="_blank" rel="noopener noreferrer" className="block text-center px-4 py-2 bg-[#F0EFF4] hover:bg-[#7B61FF] hover:text-white text-[#0A0A14] text-xs font-bold rounded-lg transition-colors">
                                Email Vito
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BuildStatus;
